import { collection, doc, getDocs, getDoc, updateDoc, deleteDoc, addDoc, query, where, orderBy, limit, startAfter, getCountFromServer, Timestamp } from "firebase/firestore";
import { db } from "./firebase";


const firebaseDataProvider = {
    getList: async (resource, params) => {
        const { filter, pagination, sort } = params;
        const { page, perPage } = pagination;

        const searchFieldsMap = {
            categories: ["name"],
            machines: ["machineUUID", "location", "versionName"],
            products: ["name"],
            users: ["firstName", "lastName", "email", "client.companyName"],
            employees: ["fullName", "employeeNumber", "cardNumber"],
            // Add other resources if needed
        };

        const { q: qSearch = "", ...restFilters } = filter;
        const collectionRef = collection(db, resource);
        const fields = searchFieldsMap[resource] || [];

        // Products: filter by client, then search/sort/paginate in memory
        if (resource === "products") {
            const clientId = filter.client;
            const searchText = (filter.name || qSearch || "").toLowerCase();

            let q = collection(db, "products");
            if (clientId) {
                q = query(q, where("client", "==", clientId));
            }

            const snapshot = await getDocs(q,limit(500));
            let data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (clientId) {
                data = data.filter(doc => doc.client === clientId);
            }

            if (searchText) {
                data = data.filter(doc => doc.name?.toLowerCase().includes(searchText));
            }

        
            const start = (page - 1) * perPage;
            return {
                data: data.slice(start, start + perPage),
                total: data.length,
            };
        }

        if (qSearch) {
            let searchQuery = collectionRef;
            for (const [key, value] of Object.entries(restFilters)) {
                if (value !== undefined && value !== null && value !== "") {
                    searchQuery = query(searchQuery, where(key, "==", value));
                }
            }

            const snapshot = await getDocs(searchQuery);
            const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            let totalQty = 0;
            let totalCredit = 0;
            const searchLower = qSearch.toLowerCase();
            const filtered = allDocs.filter(doc => {
                const match = fields.some(field => {
                    const value = field.split('.').reduce((obj, key) => obj && obj[key], doc);
                    return value && value.toString().toLowerCase().includes(searchLower);
                });

                if (match) {
                    totalQty += doc.totalQty || 0;
                    totalCredit += doc.totalCredit || 0;
                }

                return match;
            });
    
            // Sort manually
            if (sort?.field && sort?.order) {
                filtered.sort((a, b) => {
                    const aVal = a[sort.field] ?? "";
                    const bVal = b[sort.field] ?? "";
                    return sort.order === "ASC"
                        ? aVal.localeCompare?.(bVal) ?? 0
                        : bVal.localeCompare?.(aVal) ?? 0;
                });
            }
    
            // Paginate manually
            const start = (page - 1) * perPage;
            const end = start + perPage;
    
            return {
                data: filtered.slice(start, end),
                total: filtered.length,
                aggregate: { totalQty, totalCredit },
            };
        };

        // Employees: Firestore's orderBy drops documents that are missing the sort
        // field, which hides employees without a createdAt. Sort in memory instead.
        if (resource === "employees") {
            let employeeQuery = collectionRef;
            Object.entries(restFilters).forEach(([key, val]) => {
                if (val !== undefined && val !== null && val !== "") {
                    employeeQuery = query(employeeQuery, where(key, "==", val));
                }
            });

            const snapshot = await getDocs(employeeQuery);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            if (sort?.field && sort?.order) {
                const sortValue = (value) => {
                    if (value?.toDate) return value.toDate().getTime();
                    if (value?.seconds) return value.seconds * 1000;
                    return value ?? "";
                };

                data.sort((a, b) => {
                    const aVal = sortValue(a[sort.field]);
                    const bVal = sortValue(b[sort.field]);
                    if (aVal === bVal) return 0;
                    const compared = aVal > bVal ? 1 : -1;
                    return sort.order === "ASC" ? compared : -compared;
                });
            }

            const start = (page - 1) * perPage;

            return {
                data: data.slice(start, start + perPage),
                total: data.length,
            };
        }

        if (resource === "transactions") {
            const whereConditions = [];

            Object.entries(restFilters).forEach(([key, val]) => {
                if (val !== undefined && val !== "" && val !== null) {
                    if (key === "machineId") {
                        whereConditions.push(where("machine.id", "==", val));
                    } else if (key === "status" && val !== "2") {
                        whereConditions.push(where("status", "==", parseInt(val)));
                    } else if (key === "dateStart") {
                        const date = new Date(val);
                        whereConditions.push(where("date", ">=", Timestamp.fromDate(date)));
                    } else if (key === "dateEnd") {
                        const date = new Date(val);
                        date.setHours(23, 59, 59, 999);
                        whereConditions.push(where("date", "<=", Timestamp.fromDate(date)));
                    } else {
                        whereConditions.push(where(key, "==", val));
                    }
                }
            });
        
            let q = query(collection(db, resource), ...whereConditions);
        
            if (sort?.field && sort?.order) {
                q = query(q, orderBy(sort.field, sort.order.toLowerCase()));
            }
        
            const totalSnapshot = await getCountFromServer(q);
            const total = totalSnapshot.data().count;
        
            if (page > 1) {
                const offset = (page - 1) * perPage;
                const previousSnapshot = await getDocs(query(q, limit(offset)));
                const docs = previousSnapshot.docs;
                const lastVisible = docs[docs.length - 1];
                q = query(q, startAfter(lastVisible), limit(perPage));
            } else {
                q = query(q, limit(perPage));
            }
        
            const paginatedSnapshot = await getDocs(q);
            const data = paginatedSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const totalCredit = data.reduce((sum, doc) => sum + (parseFloat(doc.amount) || 0), 0);
            
            return {
                data,
                total,
                aggregate: {
                    totalCredit: parseFloat(totalCredit.toFixed(2))
                }
            };
        };

        if (resource === "transactions-employee") {
            const snapshot = await getDocs(collection(db, "transactions"));
            const transactions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // Group by employee
            const groupedMap = transactions.reduce((acc, tx) => {
                const empId = tx.employee?.id;
                const empName = tx.employee?.fullName || "Unknown";
                const company = tx.client || "Unknown";
        
                if (!empId) return acc;
        
                if (!acc[empId]) {
                    acc[empId] = {
                        id: empId,
                        employeeName: empName,
                        client: company,
                        totalTransactions: 0,
                        totalCredit: 0,
                    };
                }
        
                acc[empId].totalTransactions += 1;
        
                if (tx.mode === "mro") {
                    acc[empId].totalCredit += parseFloat(tx.amount || 0);
                }
        
                return acc;
            }, {});
        
            const grouped = Object.values(groupedMap).map(e => ({
                ...e,
                totalCredit: parseFloat(e.totalCredit.toFixed(2)),
            }));
            
            // Sort
            if (sort?.field && sort?.order) {
                grouped.sort((a, b) => {
                    const aVal = a[sort.field] ?? "";
                    const bVal = b[sort.field] ?? "";
                    return sort.order === "ASC"
                        ? aVal.toString().localeCompare(bVal.toString())
                        : bVal.toString().localeCompare(aVal.toString());
                });
            }
        
            // Paginate
            const total = grouped.length;
            const start = (page - 1) * perPage;
            const end = start + perPage;
            const paginated = grouped.slice(start, end);
        
            return {
                data: paginated,
                total,
            };
        }

        if (resource === "clients" && filter?.companyName) {
            const snapshot = await getDocs(collection(db, resource));
            const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
            const filtered = allDocs.filter(doc =>
                doc.companyName?.toLowerCase().includes(filter.companyName.toLowerCase())
            );
        
            const start = (page - 1) * perPage;
            const end = start + perPage;
        
            return {
                data: filtered.slice(start, end),
                total: filtered.length,
            };
        }
    
        let whereConditions = [];
        // Apply structured filters
        Object.entries(restFilters).forEach(([key, val]) => {
            if (val !== undefined && val !== null && val !== "") {
                if (key === "machineId") {
                    whereConditions.push(where("machine.id", "==", val));
                } else if (key === "dateStart") {
                    whereConditions.push(where("date", ">=", new Date(val)));
                } else if (key === "dateEnd") {
                    whereConditions.push(where("date", "<=", new Date(val)));
                } else {
                    whereConditions.push(where(key, "==", val));
                }
            }
        });

        // Normal query (structured filtering, sorting, and paginated)
        let q = query(collectionRef, ...whereConditions);
    
        // Sorting
        if (sort?.field && sort?.order) {
            q = query(q, orderBy(sort.field, sort.order.toLowerCase()));
        }
    
        // Get total count (before pagination)
        const totalSnapshot = await getCountFromServer(q);
        const total = totalSnapshot.data().count;
    
        // Pagination
        if (page > 1) {
            const offset = (page - 1) * perPage;
            const previousSnapshot = await getDocs(query(q, limit(offset)));
            const docs = previousSnapshot.docs;
            if (docs.length > 0) {
                const lastVisible = docs[docs.length - 1];
                q = query(q, startAfter(lastVisible), limit(perPage));
            } else {
                q = query(q, limit(perPage));
            }
        } else {
            q = query(q, limit(perPage));
        }
    
        const paginatedSnapshot = await getDocs(q);
        const data = paginatedSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
        return {
            data,
            total,
        };
    },
    getOne: async (resource, params) => {
        const docRef = doc(db, resource, params.id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
            return { data: { id: docSnap.id, ...docSnap.data() } };
        } else {
            throw new Error('Document not found');
        }
    },
    create: async (resource, params) => {
        const collectionRef = collection(db, resource);
        const newDoc = await addDoc(collectionRef, params.data);
        
        return {
            data: { ...params.data, id: newDoc.id },
        };
    },
    update: async (resource, params) => {
        const docRef = doc(db, resource, params.id);
        await updateDoc(docRef, params.data);
        
        return {
            data: { ...params.data, id: params.id },
        };
    },
    delete: async (resource, params) => {
        const docRef = doc(db, resource, params.id);
        await deleteDoc(docRef);
        
        return { data: { id: params.id } };
    },
    deleteMany: async (resource, params) => {
        await Promise.all(
            params.ids.map(id => deleteDoc(doc(db, resource, id)))
        );
        return { data: params.ids };
    },
    getMany: async (resource, params) => {
        const docs = await Promise.all(
            params.ids.map(id => getDoc(doc(db, resource, id)))
        );
        const data = docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));
        
        return { data };
    },
}

export default firebaseDataProvider;
